import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Descriptions, Empty, Tabs } from 'antd';
import * as echarts from 'echarts';

import { ze } from 'utils/zhEn';

const HEAT_COLORS = ['#dacd57', '#e0b251', '#e7974b', '#eb8547', '#f45c3d'];
const UNSYMBOLICATED_COLOR = '#b0b0b0';
const ROOT_COLOR = '#e3a34e';

const MIN_BLOCK_HEIGHT = 20;   // px，每层 block 的最小高度
const GRID_VERTICAL_PADDING = 40; // grid top(20) + bottom(20)

const META_LABELS = {
  appVersion: ze('App 版本', 'App Version'),
  deviceType: ze('设备类型', 'Device Type'),
  osVersion: ze('系统版本', 'OS Version'),
  platformVersion: ze('系统版本', 'OS Version'),
  cpuTime: ze('CPU 时间', 'CPU Time'),
  writesCaused: ze('写入次数', 'Writes Caused'),
};

function heightOfTree(node) {
  const children = node?.children || [];
  if (children.length === 0) {
    return 0;
  }
  return 1 + Math.max(...children.map(child => heightOfTree(child)));
}

function filterTreeByPath(json, pathList = []) {
  if (pathList.length <= 1) {
    return json;
  }
  const pathString = pathList.join();
  const pathDataTree = structuredClone(json);
  const attachPath = (item, parent = null) => {
    item.pathList = parent ? parent.pathList.concat(item.name) : [item.name];
    for (const child of item.children || []) {
      attachPath(child, item);
    }
  };
  attachPath(pathDataTree);

  const findNode = (item) => {
    if (item.pathList.join() === pathString) {
      return item;
    }
    for (const child of item.children || []) {
      const matched = findNode(child);
      if (matched) {
        item.children = [matched];
        item.value = matched.value;
        return item;
      }
    }
    return null;
  };
  return findNode(pathDataTree);
}

function buildFlameSeriesData(rootNode, pathList) {
  const filteredJson = filterTreeByPath(structuredClone(rootNode), pathList);
  if (!filteredJson || !filteredJson.value) {
    return [];
  }

  const rootVal = rootNode.value;
  const flameData = [];

  const recur = (item, start = 0, level = 0, parent = null) => {
    const { value, name, binaryName, address, symbolicated } = item;
    const colorIndex = Math.floor((value / rootVal) / 0.2);
    const heatColor = HEAT_COLORS[colorIndex] || HEAT_COLORS[0];
    const color = level === 0
      ? ROOT_COLOR
      : (symbolicated ? heatColor : UNSYMBOLICATED_COLOR);

    const temp = {
      name,
      value: [
        level,
        start,
        start + value,
        name,
        (value / rootVal) * 100,
        binaryName || '',
        address || '',
        symbolicated ? 1 : 0,
        value,
      ],
      pathList: level === 0 ? [name] : parent.pathList.concat(name),
      itemStyle: { color },
    };
    flameData.push(temp);

    let prevStart = start;
    for (const child of item.children || []) {
      recur(child, prevStart, level + 1, temp);
      prevStart += child.value;
    }
  };

  recur(filteredJson);
  return flameData;
}

const MetricKitFlameGraphChart = ({ rootNode }) => {
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);
  const originalMaxLevelRef = useRef(0);

  // 根据树深度计算所需的图表总高度，保证每层 block 至少 MIN_BLOCK_HEIGHT px
  const chartHeight = useMemo(() => {
    const levels = heightOfTree(rootNode);
    return Math.max(300, (levels + 1) * MIN_BLOCK_HEIGHT + GRID_VERTICAL_PADDING);
  }, [rootNode]);

  const renderItem = useCallback((params, api) => {
    const level = api.value(0);
    const start = api.coord([api.value(1), level]);
    const end = api.coord([api.value(2), level]);
    const bandHeight = ((api.size && api.size([0, 1])) || [0, 20])[1];
    const width = end[0] - start[0];

    return {
      type: 'rect',
      transition: ['shape'],
      shape: {
        x: start[0],
        y: start[1] - bandHeight / 2,
        width,
        height: bandHeight - 2,
        r: 2,
      },
      style: {
        fill: api.visual('color'),
      },
      emphasis: {
        style: {
          stroke: '#000',
        },
      },
      textConfig: {
        position: 'insideLeft',
      },
      textContent: {
        style: {
          text: api.value(3),
          fontFamily: 'Verdana',
          fill: '#000',
          width: width - 4,
          overflow: 'truncate',
          ellipsis: '..',
          truncateMinChar: 1,
        },
        emphasis: {
          style: {
            stroke: '#000',
            lineWidth: 0.5,
          },
        },
      },
    };
  }, []);

  useEffect(() => {
    if (!rootNode?.value || !chartRef.current) {
      return undefined;
    }

    originalMaxLevelRef.current = heightOfTree(rootNode);

    const baseOption = {
      tooltip: {
        formatter: (params) => {
          const samples = params.value[8] || (params.value[2] - params.value[1]);
          const percentage = +params.value[4].toFixed(2);
          const binaryName = params.value[5];
          const address = params.value[6];
          const symbolicated = params.value[7] === 1;
          const lines = [
            `${params.marker} ${params.value[3]}`,
            `${ze('采样数', 'Samples')}: ${echarts.format.addCommas(samples)} (${percentage}%)`,
            `${ze('符号还原', 'Symbolicated')}: ${symbolicated ? ze('是', 'Yes') : ze('否', 'No')}`,
          ];
          if (binaryName) {
            lines.push(`${ze('二进制', 'Binary')}: ${binaryName}`);
          }
          if (address) {
            lines.push(`${ze('地址', 'Address')}: ${address}`);
          }
          return `<div style="max-width:50vw;word-break:break-all;white-space:normal;">${lines.join('<br/>')}</div>`;
        },
      },
      grid: {
        top: 20,
        bottom: 20,
        left: 20,
        right: 20,
      },
      xAxis: {
        show: false,
        max: rootNode.value,
      },
      yAxis: {
        show: false,
        max: originalMaxLevelRef.current,
        min: 0,
        inverse: true,
      },
      series: [
        {
          type: 'custom',
          renderItem,
          encode: {
            x: [1, 2],
            y: 0,
          },
          data: buildFlameSeriesData(rootNode),
          animation: false,
          animationDuration: 0,
        },
      ],
    };

    const chart = echarts.init(chartRef.current);
    chartInstanceRef.current = chart;
    chart.setOption(baseOption);

    const onClick = (params) => {
      const zoomData = buildFlameSeriesData(rootNode, params.data.pathList);
      if (!zoomData.length) {
        return;
      }
      const zoomMax = zoomData[0].value[2];
      chart.setOption({
        xAxis: { max: zoomMax, show: false },
        yAxis: {
          show: false,
          max: originalMaxLevelRef.current,
          min: 0,
          inverse: true,
        },
        series: {
          type: 'custom',
          renderItem,
          encode: {
            x: [1, 2],
            y: 0,
          },
          data: zoomData,
        },
      });
    };

    chart.on('click', onClick);

    return () => {
      chart.off('click', onClick);
      chart.dispose();
      chartInstanceRef.current = null;
    };
  }, [renderItem, rootNode]);

  return (
    <div
      ref={chartRef}
      style={{
        width: '100%',
        height: chartHeight,
      }}
    />
  );
};

MetricKitFlameGraphChart.propTypes = {
  rootNode: PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.number,
    children: PropTypes.array,
  }),
};

function formatMetaValue(key, value) {
  if (value == null || value === '') {
    return '-';
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch (e) {
      return String(value);
    }
  }
  return String(value);
}

function buildMetaItems(meta = {}) {
  const preferredKeys = ['appVersion', 'deviceType', 'osVersion', 'platformVersion', 'cpuTime', 'writesCaused'];
  const items = [];
  const usedKeys = new Set();

  preferredKeys.forEach((key) => {
    if (usedKeys.has(key)) {
      return;
    }
    if (Object.prototype.hasOwnProperty.call(meta, key)) {
      items.push({
        key,
        label: META_LABELS[key] || key,
        value: formatMetaValue(key, meta[key]),
      });
      usedKeys.add(key);
      // osVersion 和 platformVersion 含义相同，取到一个后跳过另一个
      if (key === 'osVersion') usedKeys.add('platformVersion');
      if (key === 'platformVersion') usedKeys.add('osVersion');
    }
  });

  Object.keys(meta).forEach((key) => {
    if (usedKeys.has(key)) {
      return;
    }
    items.push({
      key,
      label: key,
      value: formatMetaValue(key, meta[key]),
    });
  });

  return items;
}

const MetricKitFlameGraph = ({ data }) => {
  const roots = data?.roots || [];
  const [activeRootKey, setActiveRootKey] = useState('0');

  useEffect(() => {
    setActiveRootKey('0');
  }, [data]);

  const metaItems = useMemo(() => buildMetaItems(data?.meta), [data?.meta]);

  const retraceRateText = useMemo(() => {
    const total = Number(data?.totalFrameCount) || 0;
    const hit = Number(data?.symbolicatedFrameCount) || 0;
    if (total <= 0) {
      return '-';
    }
    return `${hit}/${total} (${((hit / total) * 100).toFixed(1)}%)`;
  }, [data?.symbolicatedFrameCount, data?.totalFrameCount]);

  const tabItems = useMemo(() => {
    return roots.map((root, index) => {
      const label = root?.name || `${ze('线程', 'Thread')} ${index + 1}`;
      return {
        key: String(index),
        label,
        children: <MetricKitFlameGraphChart rootNode={root} />,
      };
    });
  }, [roots]);

  if (!data || roots.length === 0) {
    return <Empty description={ze('暂无火焰图数据', 'No flame graph data')} />;
  }

  const showThreadTabs = data.perThread && roots.length > 1;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      {/* 信息区：固定高度，不参与弹性分配 */}
      <div style={{ flexShrink: 0 }}>
        <Descriptions
          size="small"
          column={3}
          bordered
          style={{ marginBottom: 8 }}
        >
          <Descriptions.Item label={ze('诊断类型', 'Diagnostic Type')}>
            {data.diagnosticType || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={ze('进程名', 'Process Name')}>
            {data.processName || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={ze('总采样数', 'Total Samples')}>
            {data.totalSampleCount != null ? echarts.format.addCommas(data.totalSampleCount) : '-'}
          </Descriptions.Item>
          <Descriptions.Item label={ze('符号还原率', 'Retrace Rate')}>
            {retraceRateText}
          </Descriptions.Item>
          {data.retraceStatus != null && (
            <Descriptions.Item label={ze('还原状态码', 'Retrace Status')}>
              {data.retraceStatus}
            </Descriptions.Item>
          )}
          {metaItems.map(item => (
            <Descriptions.Item key={item.key} label={item.label}>
              {item.value}
            </Descriptions.Item>
          ))}
        </Descriptions>

        <div style={{ marginBottom: 6, color: '#666', fontSize: 12 }}>
          {ze('点击火焰图块可放大查看；灰色表示未成功符号还原', 'Click a block to zoom in; gray blocks are not symbolicated')}
        </div>
      </div>

      {/* 图表区：占满剩余高度，内容超出则纵向滚动；禁横向滚动防止纵向滚动条占宽触发横条 */}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
        {showThreadTabs ? (
          <Tabs
            activeKey={activeRootKey}
            onChange={setActiveRootKey}
            items={tabItems}
          />
        ) : (
          <MetricKitFlameGraphChart rootNode={roots[0]} />
        )}
      </div>
    </div>
  );
};

MetricKitFlameGraph.propTypes = {
  data: PropTypes.shape({
    diagnosticType: PropTypes.string,
    meta: PropTypes.object,
    perThread: PropTypes.bool,
    processName: PropTypes.string,
    totalSampleCount: PropTypes.number,
    symbolicatedFrameCount: PropTypes.number,
    totalFrameCount: PropTypes.number,
    retraceStatus: PropTypes.number,
    roots: PropTypes.arrayOf(PropTypes.object),
  }),
};

export default MetricKitFlameGraph;
